import type { IEventRecurrence } from "../types";
import type { TFunction } from "i18next";
import type { Frequency, Weekday } from "rrule";

import libheif from "libheif-js/wasm-bundle";
import { RRule, RRuleSet } from "rrule";

import {
  DEFAULT_WHITELABEL_VALID_JR_STEPS,
  FAILED_DOMAIN_STATUSES,
  SUCCESS_DOMAIN_STATUSES,
  VERIFYING_DOMAIN_STATUSES,
} from "../constants";
import {
  whiteLabelJRActive,
  whiteLabelJRStep,
  whiteLabelValidJRSteps,
} from "../graphql/globalVar/state";
import { EndRecurrenceEnum, RepeatPeriodEnum } from "../types";
import { formatDateDisplay } from "../utils/formatDate";

export const base64ToFile = (base64String: string, filename: string): File => {
  const byteString = atob(base64String.split(",")[1]);
  const mimeString = base64String.split(",")[0].split(":")[1].split(";")[0];
  const byteNumbers: number[] = new Array(byteString.length);

  for (let i = 0; i < byteString.length; i++) {
    byteNumbers[i] = byteString.charCodeAt(i);
  }

  const byteArray = new Uint8Array(byteNumbers);
  const fileType = mimeString.split("/")[1];

  return new File([byteArray], `${filename}.${fileType}`, {
    type: mimeString,
  });
};

export const imageFileToBase64 = (imageFile: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    if (!(imageFile instanceof File)) {
      return null;
    }

    const reader = new FileReader();
    reader.readAsDataURL(imageFile);
    reader.onload = () => {
      const base64String = reader.result as string;
      resolve(base64String);
    };
    reader.onerror = (error) => {
      reject(error);
    };
  });
};

export const filesToBlobs = (files: File[]): Promise<Blob[]> =>
  Promise.all(
    files.map(
      (file) =>
        new Promise<Blob>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            if (reader.result instanceof ArrayBuffer) {
              resolve(new Blob([reader.result], { type: file.type }));
            } else {
              reject(new Error("Failed to read file as ArrayBuffer."));
            }
          };
          reader.onerror = () => {
            reject(new Error("Error reading file."));
          };
          reader.readAsArrayBuffer(file);
        })
    )
  );

export const clearLocalStorageItems = () => {
  ["entity", "options", "poster"].forEach((item) => {
    localStorage.removeItem(item);
  });
};

export function formatDateTime(dateString: string): string {
  const currentDate = new Date(dateString);
  const year = currentDate.getUTCFullYear();
  const month = (currentDate.getUTCMonth() + 1).toString().padStart(2, "0");
  const day = currentDate.getUTCDate().toString().padStart(2, "0");
  const hours = currentDate.getUTCHours().toString().padStart(2, "0");
  const minutes = currentDate.getUTCMinutes().toString().padStart(2, "0");
  const formattedDateTime = `${year}-${month}-${day}T${hours}:${minutes}`;
  return formattedDateTime;
}

export const getFileFromUrl = async (fileUrl: string): Promise<File> => {
  const getUrlExtension = (url: string): string => {
    return url.split(/[#?]/)[0].split(".").pop()!.trim();
  };
  const imgExt = getUrlExtension(fileUrl);

  try {
    const response = await fetch(fileUrl);
    const blob = await response.blob();
    const file = new File([blob], "image." + imgExt, {
      type: blob.type,
    });
    return file;
  } catch (error) {
    console.error("Error fetching the file:", error);
    throw error;
  }
};

export function getDayNumber(
  date: Date,
  t: TFunction
): {
  count: number;
  nameCount: string;
} {
  const day = date.getDate();
  const n = Math.ceil(day / 7);

  if (n >= 11 && n <= 13) {
    return { count: 1, nameCount: t("firstStep.first") };
  }

  switch (n % 10) {
    case 1:
      return { count: 1, nameCount: t("firstStep.first") };
    case 2:
      return { count: 2, nameCount: t("firstStep.second") };
    case 3:
      return { count: 3, nameCount: t("firstStep.third") };
    case 4:
      return { count: 4, nameCount: t("firstStep.fourth") };
    case 5:
      return { count: 5, nameCount: t("firstStep.fifth") };
    default:
      return { count: 1, nameCount: t("firstStep.first") };
  }
}

export const getRrulePeriod = (period: RepeatPeriodEnum) => {
  switch (period) {
    case RepeatPeriodEnum.Daily:
      return RRule.DAILY;
    case RepeatPeriodEnum.Weekly:
      return RRule.WEEKLY;
    case RepeatPeriodEnum.Monthly:
      return RRule.MONTHLY;
    case RepeatPeriodEnum.Yearly:
      return RRule.YEARLY;
    default:
      RRule.WE;
      return RRule.DAILY;
  }
};

export const getRruleWeekDayFromWeekArray = (weekArr: boolean[]) => {
  const indexToWeekday = [
    RRule.MO,
    RRule.TU,
    RRule.WE,
    RRule.TH,
    RRule.FR,
    RRule.SA,
    RRule.SU,
  ];
  return weekArr
    .map((include, index) => (include ? indexToWeekday[index] : null))
    .filter((weekday) => weekday !== null);
};

export const freqRuleToPeriodEnumString = (
  rrule: RRule,
  isCustomMode = false
): string => {
  const freq = rrule.options.freq;
  switch (freq) {
    case RRule.DAILY:
      return RepeatPeriodEnum.Daily;
    case RRule.WEEKLY:
      return rrule.options.byweekday.length > 1 && !isCustomMode
        ? RepeatPeriodEnum.EveryWeekday
        : RepeatPeriodEnum.Weekly;
    case RRule.MONTHLY:
      return RepeatPeriodEnum.Monthly;
    case RRule.YEARLY:
      return RepeatPeriodEnum.Yearly;
  }
};

export const getReccurrenceFromRule = (rrule: RRule): IEventRecurrence => {
  const weekdayIndices: { [key: string]: number } = {
    MO: 0,
    TU: 1,
    WE: 2,
    TH: 3,
    FR: 4,
    SA: 5,
    SU: 6,
  };

  const byWeekdays = Array.isArray(rrule.origOptions.byweekday)
    ? rrule.origOptions.byweekday
    : rrule.origOptions.byweekday
      ? [rrule.origOptions.byweekday]
      : [];

  const weekDays = new Array(7).fill(false);
  byWeekdays.forEach((weekday: Weekday) => {
    weekDays[weekdayIndices[weekday.toString()]] = true;
  });

  return {
    repeatsEvery: rrule.options.interval,
    repeatsType: freqRuleToPeriodEnumString(rrule, true),
    weekDays,
    endRecurrence: rrule.options.count
      ? EndRecurrenceEnum.After
      : rrule.options.until
        ? EndRecurrenceEnum.OnDate
        : EndRecurrenceEnum.NeverStop,
    onDate: rrule.options.until
      ? new Date(rrule.options.until).toISOString().split("T")[0]
      : null,
    endAfterOccurences: rrule.options.count || 1,
  };
};

const RECURRENCE_FREQUENCY_KEYS: Partial<Record<Frequency, string>> = {
  [RRule.DAILY]: "daily",
  [RRule.WEEKLY]: "weekly",
  [RRule.MONTHLY]: "monthly",
  [RRule.YEARLY]: "yearly",
};

const ORDINAL_KEYS = ["first", "second", "third", "fourth", "fifth"];
const LAST_POSITION = -1;

const getPositionText = (position: number, t: TFunction) => {
  if (position === LAST_POSITION) return t("recurrence.last");
  const key = ORDINAL_KEYS[position - 1];
  return key ? t(`firstStep.${key}`) : "";
};

export const formatRecurrenceText = (
  rule: string | undefined,
  t: TFunction,
  locale: string
): string => {
  if (!rule) return "";
  let options: RRule["options"];
  try {
    options = RRule.fromString(rule).options;
  } catch {
    return "";
  }
  const { freq, interval, byweekday, bysetpos, bymonth, count, until } =
    options;
  const frequencyKey = RECURRENCE_FREQUENCY_KEYS[freq];
  if (!frequencyKey) return "";

  // 2024-01-01 is Monday, rrule weekday 0 = Monday
  const weekdayName = (day: number) =>
    new Date(2024, 0, 1 + day).toLocaleDateString(locale, { weekday: "long" });
  const days = (byweekday ?? []).map(weekdayName).join(", ");

  const parts = [
    interval > 1
      ? t(`recurrence.${frequencyKey}Interval`, { count: interval })
      : t(`recurrence.${frequencyKey}`),
  ];

  if (freq === RRule.WEEKLY && days) {
    parts.push(t("recurrence.onWeekdays", { days }));
  }
  const position = bysetpos?.length ? getPositionText(bysetpos[0], t) : "";
  if (freq === RRule.MONTHLY && days && position) {
    parts.push(t("recurrence.onNthWeekday", { position, day: days }));
  }
  if (freq === RRule.YEARLY && bymonth?.length) {
    parts.push(
      t("recurrence.inMonth", {
        month: new Date(2024, bymonth[0] - 1, 1).toLocaleDateString(locale, {
          month: "long",
        }),
      })
    );
  }
  if (count) parts.push(t("recurrence.times", { count }));
  if (until) {
    parts.push(
      t("recurrence.until", {
        date: formatDateDisplay(until.toISOString(), locale),
      })
    );
  }

  return parts.join(", ");
};

const RECURRENCE_FALLBACK_YEARS = 2;

interface IRecurrenceDates {
  rrule: string;
  startDate: string;
  exdates: { date: string }[];
  rdates: { date: string }[];
}

export const getRecurrenceDatesCount = (
  recurrence: IRecurrenceDates | null | undefined
): number => {
  if (!recurrence?.rrule) return 1;
  try {
    const dtstart = new Date(recurrence.startDate);
    const ruleOptions = RRule.parseString(recurrence.rrule);
    const rruleSet = new RRuleSet();
    rruleSet.rrule(new RRule({ ...ruleOptions, dtstart }));
    recurrence.exdates.forEach(({ date }) => rruleSet.exdate(new Date(date)));
    recurrence.rdates.forEach(({ date }) => rruleSet.rdate(new Date(date)));
    if (ruleOptions.count || ruleOptions.until) {
      return Math.max(1, rruleSet.all().length);
    }

    const limit = new Date();
    limit.setFullYear(limit.getFullYear() + RECURRENCE_FALLBACK_YEARS);
    return Math.max(1, rruleSet.between(dtstart, limit, true).length);
  } catch {
    return 1;
  }
};

export const isLocalStorageData = () =>
  localStorage.getItem("entity") &&
  localStorage.getItem("poster") &&
  localStorage.getItem("options");

export const hexToRgba = (hex: string, alpha = 1): string | null => {
  const match = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!match) return null;
  const [, r, g, b] = match.map((v) => parseInt(v, 16));
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const rgbaToHex = (rgba: string): string | null => {
  const match = /^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)$/i.exec(
    rgba
  );
  if (!match) return null;
  const [, r, g, b] = match.map((v) =>
    Math.round(parseFloat(v)).toString(16).padStart(2, "0")
  );
  return `#${r}${g}${b}`;
};

export const getDomainStatus = (status: string) => {
  if (VERIFYING_DOMAIN_STATUSES.includes(status))
    return { className: "text-warning", text: "Verifying" };
  if (FAILED_DOMAIN_STATUSES.includes(status))
    return { className: "text-danger", text: "Error" };
  if (SUCCESS_DOMAIN_STATUSES.includes(status))
    return { className: "text-dark", text: "Connected" };
  return { className: "", text: "" };
};

export const generateId = (prefix: string) =>
  `${prefix}-${Date.now()}-${Math.floor(Math.random() * 10000)}`;

export const resetWhiteLabelJR = () => {
  whiteLabelJRStep(0);
  whiteLabelJRActive(false);
  whiteLabelValidJRSteps(DEFAULT_WHITELABEL_VALID_JR_STEPS);
};

export const convertHeicToJpg = async (file: File): Promise<File> => {
  const isHeic =
    file.type === "image/heic" ||
    file.type === "image/heif" ||
    file.name.toLowerCase().endsWith(".heic") ||
    file.name.toLowerCase().endsWith(".heif");

  if (!isHeic) return file;

  try {
    const arrayBuffer = await file.arrayBuffer();
    const decoder = new libheif.HeifDecoder();
    const data = decoder.decode(arrayBuffer);

    const image = data[0];
    const width = image.get_width();
    const height = image.get_height();

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Failed to get canvas context");

    const imageData = ctx.createImageData(width, height);
    await new Promise((resolve, reject) => {
      image.display(imageData, (displayData: ImageData | null) => {
        displayData
          ? resolve(null)
          : reject(new Error("HEIF processing error"));
      });
    });
    ctx.putImageData(imageData, 0, 0);

    const targetSize = file.size;
    let quality = 0.9;
    const minQuality = 0.05;
    const step = 0.05;
    let blob: Blob | null = null;

    while (quality >= minQuality) {
      blob = await new Promise<Blob>((resolve) =>
        canvas.toBlob((b) => resolve(b!), "image/jpeg", quality)
      );

      if (blob.size <= targetSize) break;
      quality -= step;
    }

    return new File([blob!], file.name.replace(/\.[^/.]+$/, ".jpg"), {
      type: "image/jpeg",
    });
  } catch (err) {
    console.error("HEIC to JPG conversion failed:", err);
    return file;
  }
};

export async function base64ToFileСompressToMaxSize(
  base64: string,
  maxBytes: number,
  filename: string
): Promise<File> {
  const [header] = base64.split(",");
  const mimeString = header.split(":")[1].split(";")[0];
  const extension = mimeString.split("/")[1];

  const img = new Image();
  img.src = base64;
  await new Promise((res) => (img.onload = res));

  const canvas = document.createElement("canvas");
  canvas.width = img.width;
  canvas.height = img.height;
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(img, 0, 0);

  let quality = 0.9;
  const minQuality = 0.05;
  const step = 0.05;
  let blob: Blob | null = null;

  while (quality >= minQuality) {
    blob = await new Promise<Blob>((resolve) =>
      canvas.toBlob((b) => resolve(b!), mimeString, quality)
    );

    if (blob.size <= maxBytes) break;
    quality -= step;
  }

  return new File([blob!], `${filename}.${extension}`, { type: mimeString });
}

export const toISODuration = (time: string, days: string) => {
  const [hoursStr, minutesStr] = time.split(":");
  const hours = parseInt(hoursStr, 10) || 0;
  const minutes = parseInt(minutesStr, 10) || 0;
  const d = parseInt(days, 10) || 0;

  let iso = "P";
  if (d > 0) iso += `${d}D`;

  if (hours > 0 || minutes > 0) {
    iso += "T";
    if (hours > 0) iso += `${hours}H`;
    if (minutes > 0) iso += `${minutes}M`;
  }

  return iso;
};

export const fromISODuration = (iso: string) => {
  const regex = /^P(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?)?$/;

  const match = iso.match(regex);

  if (!match) {
    throw new Error(`Invalid ISO 8601 duration: ${iso}`);
  }

  const [, daysStr, hoursStr, minutesStr] = match;

  const days = daysStr || "0";
  const hours = hoursStr ? hoursStr.padStart(2, "0") : "00";
  const minutes = minutesStr ? minutesStr.padStart(2, "0") : "00";

  return {
    days,
    time: `${hours}:${minutes}`,
  };
};

export const diffDatesToDaysTime = (date1: string, date2: string) => {
  const start = new Date(date1).getTime();
  const end = new Date(date2).getTime();

  let diffMs = end - start;

  const negative = diffMs < 0;
  if (negative) diffMs = Math.abs(diffMs);

  const totalMinutes = Math.floor(diffMs / (1000 * 60));
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;

  const daysStr = String(days);
  const timeStr = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;

  return {
    days: negative ? `-${daysStr}` : daysStr,
    time: timeStr,
  };
};

export const getTotalMinutes = (days: string, time: string) => {
  const dayCount = Number(days);
  if (isNaN(dayCount)) {
    throw new Error("Invalid days value");
  }

  const [hoursStr, minutesStr] = time.split(":");
  const hours = Number(hoursStr);
  const minutes = Number(minutesStr);

  if (
    [hours, minutes].some(isNaN) ||
    hours < 0 ||
    hours > 23 ||
    minutes < 0 ||
    minutes > 59
  ) {
    throw new Error("Invalid time format");
  }

  const totalMinutes = dayCount * 24 * 60 + hours * 60 + minutes;
  return totalMinutes;
};
